import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Edit2, Check, X, Search, Wallet, CreditCard, Plus, Eye, Image, ExternalLink, Trash2, BarChart3, Phone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import DosarePerConsilier from "@/components/DosarePerConsilier";
import AfiliatDetailPanel from "@/components/AfiliatDetailPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { adminToken, logoutAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState("dosare");
  const [stats, setStats] = useState(null);
  const [dosare, setDosare] = useState([]);
  const [afiliati, setAfiliati] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCode, setEditingCode] = useState(null);
  const [newCode, setNewCode] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [selectedDosar, setSelectedDosar] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [consilieri, setConsilieri] = useState([]);
  const [leads, setLeads] = useState([]);
  const [showAddConsilier, setShowAddConsilier] = useState(false);
  const [consilierForm, setConsilierForm] = useState({ nume: "", email: "", telefon: "", parola: "" });
  const [showCreateDosar, setShowCreateDosar] = useState(false);
  const [dosarForm, setDosarForm] = useState({ nume_prenume: "", telefon: "", email: "", oras: "", tip_dauna: "", detalii: "" });
  const [dosarSubTab, setDosarSubTab] = useState("toate");
  const [selectedAfiliat, setSelectedAfiliat] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${adminToken}` };
      const [statsRes, dosareRes, afiliatiRes, consilieriRes, leadsRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { headers }).then(r => r.json()),
        fetch(`${API}/admin/dosare`, { headers }).then(r => r.json()),
        fetch(`${API}/admin/afiliati`, { headers }).then(r => r.json()),
        fetch(`${API}/admin/consilieri`, { headers }).then(r => r.json()),
        fetch(`${API}/admin/leads`, { headers }).then(r => r.json())
      ]);
      setStats(statsRes);
      setDosare(Array.isArray(dosareRes) ? dosareRes : []);
      setConsilieri(Array.isArray(consilieriRes) ? consilieriRes : []);
      setLeads(Array.isArray(leadsRes) ? leadsRes : []);
      
      const afiliatiWithPayments = await Promise.all(
        afiliatiRes.map(async (a) => {
          try {
            const payRes = await fetch(`${API}/admin/afiliati/${a.id}/payments`, { headers });
            if (payRes.ok) {
              const payData = await payRes.json();
              return { ...a, ...payData };
            }
          } catch (error) {
            console.error(`Payment fetch failed for ${a.id}:`, error);
          }
          return a;
        })
      );
      setAfiliati(afiliatiWithPayments);
    } catch (err) {
      console.error("Error loading dashboard:", err);
      toast.error("Eroare la încărcarea datelor");
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    if (!adminToken) {
      navigate("/admin/login");
      return;
    }
    fetchData();
  }, [adminToken, navigate, fetchData]);

  const updateStatus = async (dosarId, status) => {
    try {
      const response = await fetch(`${API}/admin/dosare/${dosarId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ status })
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || "Failed");
      }
      toast.success("Status actualizat!");
      fetchData();
    } catch (err) {
      toast.error(err.message || "Eroare");
    }
  };

  const addConsilier = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/admin/consilieri`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify(consilierForm)
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Eroare"); }
      toast.success("Consilier creat!");
      setShowAddConsilier(false);
      setConsilierForm({ nume: "", email: "", telefon: "", parola: "" });
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  const toggleConsilier = async (id) => {
    try {
      await fetch(`${API}/admin/consilieri/${id}/toggle`, { method: "PATCH", headers: { Authorization: `Bearer ${adminToken}` } });
      fetchData();
    } catch { toast.error("Eroare"); }
  };

  const deleteConsilier = async (id) => {
    if (!window.confirm("Sigur doriti sa stergeti acest consilier?")) return;
    try {
      const res = await fetch(`${API}/admin/consilieri/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${adminToken}` } });
      if (!res.ok) throw new Error("Eroare");
      toast.success("Consilier sters!");
      fetchData();
    } catch { toast.error("Eroare la stergere"); }
  };

  const deleteAfiliat = async (id) => {
    if (!window.confirm("Sigur doriti sa stergeti acest afiliat? Platile asociate vor fi sterse.")) return;
    try {
      const res = await fetch(`${API}/admin/afiliati/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${adminToken}` } });
      if (!res.ok) throw new Error("Eroare");
      toast.success("Afiliat sters!");
      fetchData();
    } catch { toast.error("Eroare la stergere"); }
  };

  const createDosarDirect = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/admin/dosare/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify(dosarForm)
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Eroare"); }
      const data = await res.json();
      toast.success(`Dosar creat: ${data.numar_dosar}`);
      setShowCreateDosar(false);
      setDosarForm({ nume_prenume: "", telefon: "", email: "", oras: "", tip_dauna: "", detalii: "" });
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  const updateAfiliatCode = async (afiliatId) => {
    if (!newCode || newCode.length < 4) {
      toast.error("Codul trebuie să aibă minim 4 caractere");
      return;
    }
    try {
      const response = await fetch(`${API}/admin/afiliati/${afiliatId}/code`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ cod_afiliat: newCode.toUpperCase() })
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed");
      }
      toast.success("Cod actualizat!");
      setEditingCode(null);
      setNewCode("");
      fetchData();
    } catch (err) {
      toast.error(err.message || "Eroare la actualizare");
    }
  };

  const toggleAfiliatStatus = async (afiliatId) => {
    try {
      await fetch(`${API}/admin/afiliati/${afiliatId}/toggle`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success("Status actualizat!");
      fetchData();
    } catch (err) {
      toast.error("Eroare");
    }
  };

  const createPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error("Introdu o sumă validă");
      return;
    }
    try {
      await fetch(`${API}/admin/payments`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ afiliat_id: showPaymentModal.id, suma: parseFloat(paymentAmount), nota: paymentNote || null })
      });
      toast.success("Plată înregistrată!");
      setShowPaymentModal(null);
      setPaymentAmount("");
      setPaymentNote("");
      fetchData();
    } catch (err) {
      toast.error("Eroare la înregistrarea plății");
    }
  };

  const getDocumentUrl = (doc) => {
    return `${API}/admin/documents/${doc.id}?token=${adminToken}`;
  };
  
  const isImageFile = (doc) => {
    const ext = doc.extensie || doc.tip || "";
    const lower = ext.toLowerCase();
    return lower.includes("jpg") || lower.includes("jpeg") || lower.includes("png") || lower.includes("gif") || lower.includes("image");
  };

  const openDocument = (doc) => {
    if (isImageFile(doc)) {
      setSelectedImage(doc);
    } else {
      window.open(getDocumentUrl(doc), "_blank");
    }
  };

  const statusOptions = ["nou", "in_verificare", "acceptat", "in_lucru", "finalizat", "respins"];
  const statusLabels = { nou: "Nou", in_verificare: "În verificare", acceptat: "Acceptat", in_lucru: "În lucru", finalizat: "Finalizat", respins: "Respins" };

  const filterDosare = () => {
    if (!searchQuery) return dosare;
    const q = searchQuery.toLowerCase();
    return dosare.filter(d => {
      const nd = d.numar_dosar || "";
      const np = d.nume_prenume || "";
      const tel = d.telefon || "";
      const cod = d.cod_afiliat || "";
      return nd.toLowerCase().includes(q) || np.toLowerCase().includes(q) || tel.includes(q) || cod.toLowerCase().includes(q);
    });
  };

  const filterAfiliati = () => {
    if (!searchQuery) return afiliati;
    const q = searchQuery.toLowerCase();
    return afiliati.filter(a => {
      const nume = a.nume || "";
      const prenume = a.prenume || "";
      const email = a.email || "";
      const cod = a.cod_afiliat || "";
      return nume.toLowerCase().includes(q) || prenume.toLowerCase().includes(q) || email.toLowerCase().includes(q) || cod.toLowerCase().includes(q);
    });
  };

  const filteredDosare = filterDosare();
  const filteredAfiliati = filterAfiliati();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900" data-testid="admin-dashboard">
      <header className="bg-gray-800 border-b border-gray-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <span className="font-bold text-white">Admin Panel - AutoPro Daune</span>
            <button onClick={() => { logoutAdmin(); navigate("/"); }} className="text-gray-400 hover:text-white flex items-center gap-2" data-testid="admin-logout">
              <LogOut className="w-5 h-5" /> Ieșire
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-sm">Total Dosare</p>
            <p className="text-3xl font-bold text-white">{stats?.total_dosare || 0}</p>
          </div>
          <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-sm">Leads Noi</p>
            <p className="text-3xl font-bold text-amber-400">{stats?.leads_noi || 0}</p>
          </div>
          <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-sm">Finalizate</p>
            <p className="text-3xl font-bold text-emerald-400">{stats?.dosare_finalizate || 0}</p>
          </div>
          <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-sm">Total Despagubiri</p>
            <p className="text-2xl font-bold text-emerald-400">{(stats?.total_suma_despagubiri || 0).toLocaleString('ro-RO')} lei</p>
          </div>
        </div>

        <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
          <div className="bg-gray-800 p-3 rounded-xl border border-gray-700 text-center">
            <p className="text-gray-500 text-xs">Noi</p>
            <p className="text-lg font-bold text-blue-400">{stats?.dosare_noi || 0}</p>
          </div>
          <div className="bg-gray-800 p-3 rounded-xl border border-gray-700 text-center">
            <p className="text-gray-500 text-xs">In verificare</p>
            <p className="text-lg font-bold text-yellow-400">{stats?.dosare_in_verificare || 0}</p>
          </div>
          <div className="bg-gray-800 p-3 rounded-xl border border-gray-700 text-center">
            <p className="text-gray-500 text-xs">Acceptate</p>
            <p className="text-lg font-bold text-emerald-400">{stats?.dosare_acceptate || 0}</p>
          </div>
          <div className="bg-gray-800 p-3 rounded-xl border border-gray-700 text-center">
            <p className="text-gray-500 text-xs">In lucru</p>
            <p className="text-lg font-bold text-purple-400">{stats?.dosare_in_lucru || 0}</p>
          </div>
          <div className="bg-gray-800 p-3 rounded-xl border border-gray-700 text-center">
            <p className="text-gray-500 text-xs">Respinse</p>
            <p className="text-lg font-bold text-red-400">{stats?.dosare_respinse || 0}</p>
          </div>
          <div className="bg-gray-800 p-3 rounded-xl border border-gray-700 text-center">
            <p className="text-gray-500 text-xs">Afiliati / Consilieri</p>
            <p className="text-lg font-bold text-blue-400">{stats?.afiliati_activi || 0} / {stats?.total_consilieri || 0}</p>
          </div>
        </div>

        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Caută după ID, nume, telefon sau cod..." className="w-full h-12 pl-12 pr-4 rounded-xl border border-gray-700 bg-gray-800 text-white placeholder-gray-500 focus:border-emerald-500" data-testid="admin-search" />
          </div>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          <button onClick={() => setActiveTab("leads")} className={`px-6 py-2 rounded-full font-medium transition-colors ${activeTab === "leads" ? "bg-amber-500 text-white" : "bg-gray-800 text-gray-400 hover:text-white"}`} data-testid="admin-tab-leads">
            Leads ({leads.filter(l => !l.preluat).length})
          </button>
          <button onClick={() => setActiveTab("dosare")} className={`px-6 py-2 rounded-full font-medium transition-colors ${activeTab === "dosare" ? "bg-emerald-500 text-white" : "bg-gray-800 text-gray-400 hover:text-white"}`} data-testid="admin-tab-dosare">
            Dosare ({filteredDosare.length})
          </button>
          <button onClick={() => setActiveTab("afiliati")} className={`px-6 py-2 rounded-full font-medium transition-colors ${activeTab === "afiliati" ? "bg-emerald-500 text-white" : "bg-gray-800 text-gray-400 hover:text-white"}`} data-testid="admin-tab-afiliati">
            Afiliati ({filteredAfiliati.length})
          </button>
          <button onClick={() => setActiveTab("consilieri")} className={`px-6 py-2 rounded-full font-medium transition-colors ${activeTab === "consilieri" ? "bg-emerald-500 text-white" : "bg-gray-800 text-gray-400 hover:text-white"}`} data-testid="admin-tab-consilieri">
            Consilieri ({consilieri.length})
          </button>
          <button onClick={() => setActiveTab("per_consilier")} className={`px-6 py-2 rounded-full font-medium transition-colors ${activeTab === "per_consilier" ? "bg-purple-500 text-white" : "bg-gray-800 text-gray-400 hover:text-white"}`} data-testid="admin-tab-per-consilier">
            Dosare / Consilier
          </button>
          <button onClick={() => setShowCreateDosar(true)} className="ml-auto px-4 py-2 rounded-full bg-emerald-500 text-white font-medium hover:bg-emerald-600 flex items-center gap-1" data-testid="create-dosar-btn">
            <Plus className="w-4 h-4" />Dosar Nou
          </button>
        </div>

        {activeTab === "leads" && (
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Nume</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Telefon</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Oras</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Tip</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Data</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {leads.map(l => (
                    <tr key={l.id} className="hover:bg-gray-750">
                      <td className="px-4 py-3 text-white font-medium">{l.nume_prenume}</td>
                      <td className="px-4 py-3 text-gray-300">{l.telefon}</td>
                      <td className="px-4 py-3 text-gray-300">{l.oras || "-"}</td>
                      <td className="px-4 py-3 text-gray-300">{l.tip_dauna || "-"}</td>
                      <td className="px-4 py-3 text-gray-400">{l.created_at?.slice(0, 10)}</td>
                      <td className="px-4 py-3">{l.preluat ? <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">Preluat</span> : <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded">Nou</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "consilieri" && (
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden mb-6">
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-white font-medium">Consilieri</h3>
              <button onClick={() => setShowAddConsilier(true)} className="px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-sm hover:bg-emerald-600 flex items-center gap-1" data-testid="add-consilier-btn"><Plus className="w-4 h-4" />Adauga</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Nume</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Telefon</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Dosare</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Actiune</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {consilieri.map(c => (
                    <tr key={c.id} className="hover:bg-gray-750">
                      <td className="px-4 py-3 text-white font-medium">{c.nume}</td>
                      <td className="px-4 py-3 text-gray-300">{c.email}</td>
                      <td className="px-4 py-3 text-gray-300">{c.telefon}</td>
                      <td className="px-4 py-3 text-gray-300">{c.total_dosare || 0}</td>
                      <td className="px-4 py-3">{c.activ ? <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">Activ</span> : <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded">Inactiv</span>}</td>
                      <td className="px-4 py-3 flex gap-2">
                        <button onClick={() => toggleConsilier(c.id)} className={`text-xs px-2 py-1 rounded ${c.activ ? "bg-red-500/20 text-red-400" : "bg-emerald-500/20 text-emerald-400"}`} data-testid={`toggle-consilier-${c.id}`}>{c.activ ? "Dezactiveaza" : "Activeaza"}</button>
                        <button onClick={() => deleteConsilier(c.id)} className="text-xs px-2 py-1 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30" data-testid={`delete-consilier-${c.id}`}><Trash2 className="w-3 h-3" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "dosare" && (
          <div>
            <div className="flex gap-2 mb-4 flex-wrap" data-testid="dosare-sub-tabs">
              {[
                { key: "toate", label: "Toate", color: "bg-gray-600", count: filteredDosare.length },
                { key: "noi", label: "Noi", color: "bg-blue-500", count: filteredDosare.filter(d => d.status === "nou" || d.status === "in_verificare").length },
                { key: "aprobate", label: "Aprobate", color: "bg-purple-500", count: filteredDosare.filter(d => d.status === "acceptat" || d.status === "in_lucru").length },
                { key: "finalizate", label: "Finalizate", color: "bg-emerald-500", count: filteredDosare.filter(d => d.status === "finalizat").length },
                { key: "respinse", label: "Respinse", color: "bg-red-500", count: filteredDosare.filter(d => d.status === "respins").length },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setDosarSubTab(tab.key)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${dosarSubTab === tab.key ? `${tab.color} text-white` : "bg-gray-700/50 text-gray-400 hover:text-white"}`}
                  data-testid={`dosare-subtab-${tab.key}`}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </div>
            <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="admin-dosare-table">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Dosar</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Client</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Telefon</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Oraș</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Tip</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Cod Afiliat</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {filteredDosare
                      .filter(d => {
                        if (dosarSubTab === "toate") return true;
                        if (dosarSubTab === "noi") return d.status === "nou" || d.status === "in_verificare";
                        if (dosarSubTab === "aprobate") return d.status === "acceptat" || d.status === "in_lucru";
                        if (dosarSubTab === "finalizate") return d.status === "finalizat";
                        if (dosarSubTab === "respinse") return d.status === "respins";
                        return true;
                      })
                      .map((d) => (
                        <tr key={d.id} className="hover:bg-gray-700/50">
                          <td className="px-4 py-3 text-sm font-mono text-emerald-400 font-medium">{d.numar_dosar || d.id.slice(0,8)}</td>
                          <td className="px-4 py-3 text-sm text-white">{d.nume_prenume}</td>
                          <td className="px-4 py-3 text-sm text-gray-300">{d.telefon}</td>
                          <td className="px-4 py-3 text-sm text-gray-300">{d.oras || "-"}</td>
                          <td className="px-4 py-3 text-sm text-gray-300">{d.tip_dauna || "-"}</td>
                          <td className="px-4 py-3 text-sm font-mono text-emerald-400">{d.cod_afiliat || "-"}{d.discount > 0 && <span className="ml-2 px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs font-medium">-{d.discount}%</span>}</td>
                          <td className="px-4 py-3">
                            <select value={d.status} onChange={(e) => updateStatus(d.id, e.target.value)} className="bg-gray-600 text-white text-sm rounded px-2 py-1 border-0" data-testid={`status-select-${d.id}`}>
                              {statusOptions.map(s => <option key={s} value={s}>{statusLabels[s]}</option>)}
                            </select>
                          </td>
                          <td className="px-4 py-3">
                            <button onClick={() => setSelectedDosar(d)} className="text-emerald-400 hover:text-emerald-300" data-testid={`view-dosar-${d.id}`}>
                              <Eye className="w-5 h-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                {filteredDosare.filter(d => {
                  if (dosarSubTab === "toate") return true;
                  if (dosarSubTab === "noi") return d.status === "nou" || d.status === "in_verificare";
                  if (dosarSubTab === "aprobate") return d.status === "acceptat" || d.status === "in_lucru";
                  if (dosarSubTab === "finalizate") return d.status === "finalizat";
                  if (dosarSubTab === "respinse") return d.status === "respins";
                  return true;
                }).length === 0 && (
                  <p className="text-gray-500 text-sm text-center py-8">Niciun dosar în această categorie</p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "afiliati" && !selectedAfiliat && (
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="admin-afiliati-table">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Nume</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Telefon</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Cod Afiliat</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Dosare</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Comision</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">De plata</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Actiuni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {filteredAfiliati.map((a) => {
                    const isEditing = editingCode === a.id;
                    const isActiv = a.activ !== false;
                    return (
                      <tr key={a.id} className="hover:bg-gray-700/50 cursor-pointer" onClick={() => setSelectedAfiliat(a)}>
                        <td className="px-4 py-3 text-sm text-white">{a.prenume} {a.nume}</td>
                        <td className="px-4 py-3 text-sm text-gray-300">{a.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-300">{a.telefon || "-"}</td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          {isEditing ? (
                            <div className="flex items-center gap-2">
                              <input type="text" value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase())} className="bg-gray-700 text-white text-sm font-mono rounded px-2 py-1 w-24 border border-gray-600" maxLength={12} data-testid="edit-code-input" />
                              <button onClick={() => updateAfiliatCode(a.id)} className="p-1 hover:bg-emerald-500/20 rounded text-emerald-400"><Check className="w-4 h-4" /></button>
                              <button onClick={() => { setEditingCode(null); setNewCode(""); }} className="p-1 hover:bg-red-500/20 rounded text-red-400"><X className="w-4 h-4" /></button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-mono text-emerald-400">{a.cod_afiliat}</span>
                              <button onClick={() => { setEditingCode(a.id); setNewCode(a.cod_afiliat); }} className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white" data-testid={`edit-code-${a.id}`}><Edit2 className="w-3 h-3" /></button>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-300">{a.total_dosare} ({a.dosare_finalizate} fin.)</td>
                        <td className="px-4 py-3 text-sm font-medium text-emerald-400">{a.comision_total || 0} lei</td>
                        <td className="px-4 py-3 text-sm font-medium text-amber-400">{a.comision_de_plata || 0} lei</td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <button onClick={() => toggleAfiliatStatus(a.id)} className={`px-2 py-1 rounded text-xs font-medium ${isActiv ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}`} data-testid={`toggle-status-${a.id}`}>
                            {isActiv ? "Activ" : "Inactiv"}
                          </button>
                        </td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-2">
                            <button onClick={() => setSelectedAfiliat(a)} className="flex items-center gap-1 px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-xs font-medium hover:bg-emerald-500/30" data-testid={`detail-btn-${a.id}`}>
                              <Eye className="w-3 h-3" />Detalii
                            </button>
                            <button onClick={() => deleteAfiliat(a.id)} className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs hover:bg-red-500/30" data-testid={`delete-afiliat-${a.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "afiliati" && selectedAfiliat && (
          <AfiliatDetailPanel
            afiliat={selectedAfiliat}
            adminToken={adminToken}
            onBack={() => { setSelectedAfiliat(null); fetchData(); }}
            onViewDosar={(d) => setSelectedDosar(d)}
          />
        )}

        {activeTab === "per_consilier" && (
          <DosarePerConsilier consilieri={consilieri} dosare={dosare} onViewDosar={(d) => setSelectedDosar(d)} />
        )}

      </main>

      {/* Dosar Detail Modal */}
      {selectedDosar && (
        <DosarDetailModal 
          dosar={selectedDosar} 
          onClose={() => setSelectedDosar(null)} 
          statusLabels={statusLabels}
          adminToken={adminToken}
          onOpenImage={(doc) => setSelectedImage(doc)}
        />
      )}

      {/* Image Preview Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] p-4" onClick={() => setSelectedImage(null)}>
          <div className="relative max-w-4xl max-h-[90vh]">
            <button onClick={() => setSelectedImage(null)} className="absolute -top-12 right-0 text-white hover:text-gray-300 text-lg">Închide ✕</button>
            <img src={`${API}/admin/documents/${selectedImage.id}?token=${adminToken}`} alt={selectedImage.nume_original} className="max-w-full max-h-[85vh] rounded-lg object-contain" />
            <p className="text-white text-center mt-4">{selectedImage.nume_original}</p>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-2xl p-6 w-full max-w-md border border-gray-700">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center"><Wallet className="w-5 h-5 text-emerald-400" /></div>
              <div><h3 className="text-lg font-bold text-white">Inregistreaza Plata</h3><p className="text-sm text-gray-400">{showPaymentModal.prenume} {showPaymentModal.nume}</p></div>
            </div>
            <div className="bg-gray-700/50 rounded-xl p-4 mb-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-gray-400">Comision total</p><p className="text-white font-medium">{showPaymentModal.comision_total || 0} lei</p></div>
                <div><p className="text-gray-400">Deja platit</p><p className="text-blue-400 font-medium">{showPaymentModal.comision_platit || 0} lei</p></div>
                <div className="col-span-2"><p className="text-gray-400">De plata</p><p className="text-2xl text-amber-400 font-bold">{showPaymentModal.comision_de_plata || 0} lei</p></div>
              </div>
            </div>
            <div className="space-y-4">
              <div><label className="block text-sm text-gray-400 mb-1">Suma platita (lei)</label><input type="number" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} placeholder={`Max: ${showPaymentModal.comision_de_plata || 0}`} className="w-full h-12 px-4 rounded-xl border border-gray-600 bg-gray-700 text-white" data-testid="payment-amount" /></div>
              <div><label className="block text-sm text-gray-400 mb-1">Nota (optional)</label><input type="text" value={paymentNote} onChange={(e) => setPaymentNote(e.target.value)} placeholder="Ex: Transfer bancar dec 2025" className="w-full h-12 px-4 rounded-xl border border-gray-600 bg-gray-700 text-white" /></div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => { setShowPaymentModal(null); setPaymentAmount(""); setPaymentNote(""); }} className="flex-1 h-12 border border-gray-600 text-gray-300 rounded-full hover:bg-gray-700">Anuleaza</button>
              <button onClick={createPayment} className="flex-1 h-12 bg-emerald-500 text-white rounded-full hover:bg-emerald-600 flex items-center justify-center gap-2" data-testid="confirm-payment"><Plus className="w-4 h-4" />Confirma</button>
            </div>
          </div>
        </div>
      )}

      {showAddConsilier && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-2xl p-6 w-full max-w-md border border-gray-700">
            <h3 className="text-lg font-bold text-white mb-4">Adauga Consilier</h3>
            <form onSubmit={addConsilier} className="space-y-3">
              <input type="text" placeholder="Nume complet" value={consilierForm.nume} onChange={e => setConsilierForm({...consilierForm, nume: e.target.value})} className="w-full h-12 px-4 rounded-xl border border-gray-600 bg-gray-700 text-white" required data-testid="consilier-nume" />
              <input type="email" placeholder="Email" value={consilierForm.email} onChange={e => setConsilierForm({...consilierForm, email: e.target.value})} className="w-full h-12 px-4 rounded-xl border border-gray-600 bg-gray-700 text-white" required data-testid="consilier-email" />
              <input type="text" placeholder="Telefon" value={consilierForm.telefon} onChange={e => setConsilierForm({...consilierForm, telefon: e.target.value})} className="w-full h-12 px-4 rounded-xl border border-gray-600 bg-gray-700 text-white" required data-testid="consilier-telefon" />
              <input type="password" placeholder="Parola" value={consilierForm.parola} onChange={e => setConsilierForm({...consilierForm, parola: e.target.value})} className="w-full h-12 px-4 rounded-xl border border-gray-600 bg-gray-700 text-white" required data-testid="consilier-parola" />
              <div className="flex gap-3 mt-4">
                <button type="button" onClick={() => setShowAddConsilier(false)} className="flex-1 h-12 border border-gray-600 text-gray-300 rounded-full">Anuleaza</button>
                <button type="submit" className="flex-1 h-12 bg-emerald-500 text-white rounded-full" data-testid="save-consilier">Salveaza</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCreateDosar && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-2xl p-6 w-full max-w-md border border-gray-700">
            <h3 className="text-lg font-bold text-white mb-4">Creaza Dosar Nou</h3>
            <form onSubmit={createDosarDirect} className="space-y-3">
              <input type="text" placeholder="Nume si Prenume *" value={dosarForm.nume_prenume} onChange={e => setDosarForm({...dosarForm, nume_prenume: e.target.value})} className="w-full h-12 px-4 rounded-xl border border-gray-600 bg-gray-700 text-white" required data-testid="dosar-nume" />
              <input type="text" placeholder="Telefon *" value={dosarForm.telefon} onChange={e => setDosarForm({...dosarForm, telefon: e.target.value})} className="w-full h-12 px-4 rounded-xl border border-gray-600 bg-gray-700 text-white" required data-testid="dosar-telefon" />
              <input type="email" placeholder="Email" value={dosarForm.email} onChange={e => setDosarForm({...dosarForm, email: e.target.value})} className="w-full h-12 px-4 rounded-xl border border-gray-600 bg-gray-700 text-white" data-testid="dosar-email" />
              <select value={dosarForm.oras} onChange={e => setDosarForm({...dosarForm, oras: e.target.value})} className="w-full h-12 px-4 rounded-xl border border-gray-600 bg-gray-700 text-white" data-testid="dosar-oras">
                <option value="">Selecteaza Oras</option>
                <option value="București">Bucuresti</option>
                <option value="Ilfov">Ilfov</option>
              </select>
              <select value={dosarForm.tip_dauna} onChange={e => setDosarForm({...dosarForm, tip_dauna: e.target.value})} className="w-full h-12 px-4 rounded-xl border border-gray-600 bg-gray-700 text-white" data-testid="dosar-tip">
                <option value="">Selecteaza Tip Dauna</option>
                <option value="RCA">RCA</option>
                <option value="CASCO">CASCO</option>
              </select>
              <textarea placeholder="Detalii" value={dosarForm.detalii} onChange={e => setDosarForm({...dosarForm, detalii: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-gray-600 bg-gray-700 text-white" rows={3} data-testid="dosar-detalii" />
              <div className="flex gap-3 mt-4">
                <button type="button" onClick={() => setShowCreateDosar(false)} className="flex-1 h-12 border border-gray-600 text-gray-300 rounded-full">Anuleaza</button>
                <button type="submit" className="flex-1 h-12 bg-emerald-500 text-white rounded-full" data-testid="save-dosar">Creaza</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Separate component for dosar detail modal
const DosarDetailModal = ({ dosar, onClose, statusLabels, adminToken, onOpenImage }) => {
  const docs = dosar.documente || [];
  const hasDocs = docs.length > 0;

  const getDocUrl = (doc) => `${BACKEND_URL}/api/admin/documents/${doc.id}?token=${adminToken}`;
  
  const isImg = (doc) => {
    const ext = (doc.extensie || doc.tip || "").toLowerCase();
    return ext.includes("jpg") || ext.includes("jpeg") || ext.includes("png") || ext.includes("gif") || ext.includes("image");
  };

  const openDoc = (doc) => {
    if (isImg(doc)) {
      onOpenImage(doc);
    } else {
      window.open(getDocUrl(doc), "_blank");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gray-800 rounded-2xl p-6 w-full max-w-2xl border border-gray-700 my-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-white">Detalii Dosar</h3>
            <p className="text-emerald-400 font-mono">{dosar.numar_dosar}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">&times;</button>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div><p className="text-gray-400 text-sm">Nume</p><p className="text-white font-medium">{dosar.nume_prenume}</p></div>
          <div><p className="text-gray-400 text-sm">Telefon</p><p className="text-white font-medium">{dosar.telefon}</p></div>
          <div><p className="text-gray-400 text-sm">Email</p><p className="text-white">{dosar.email || "-"}</p></div>
          <div><p className="text-gray-400 text-sm">Oras</p><p className="text-white">{dosar.oras || "-"}</p></div>
          <div><p className="text-gray-400 text-sm">Tip dauna</p><p className="text-white">{dosar.tip_dauna || "-"}</p></div>
          <div><p className="text-gray-400 text-sm">Cod Afiliat</p><p className="text-emerald-400 font-mono">{dosar.cod_afiliat || "-"}</p></div>
          <div><p className="text-gray-400 text-sm">Status</p><p className="text-white">{statusLabels[dosar.status] || dosar.status}</p></div>
          {dosar.discount > 0 && <div><p className="text-gray-400 text-sm">Discount</p><p className="text-amber-400 font-bold">{dosar.discount}%</p></div>}
          <div><p className="text-gray-400 text-sm">Data creare</p><p className="text-white">{dosar.created_at?.slice(0, 10) || "-"}</p></div>
        </div>

        {(dosar.nr_auto || dosar.marca_model || dosar.asigurator) && (
          <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-gray-700 rounded-xl" data-testid="admin-vehicle-info">
            <div><p className="text-gray-400 text-xs">Nr. Auto</p><p className="text-white font-mono font-medium">{dosar.nr_auto || "-"}</p></div>
            <div><p className="text-gray-400 text-xs">Marca / Model</p><p className="text-white font-medium">{dosar.marca_model || "-"}</p></div>
            <div><p className="text-gray-400 text-xs">Asigurator</p><p className="text-white font-medium">{dosar.asigurator || "-"}</p></div>
          </div>
        )}

        {dosar.detalii && (
          <div className="mb-6">
            <p className="text-gray-400 text-sm mb-2">Detalii eveniment</p>
            <p className="text-white bg-gray-700 p-4 rounded-xl">{dosar.detalii}</p>
          </div>
        )}

        {(dosar.servicii_solicitate || []).length > 0 && (
          <div className="mb-6">
            <p className="text-gray-400 text-sm mb-2">Servicii solicitate</p>
            <div className="flex flex-wrap gap-2">
              {dosar.servicii_solicitate.map((s) => (
                <span key={s} className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm font-medium">{s.replace(/_/g, ' ')}</span>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 pt-6 border-t border-gray-700">
          <p className="text-gray-400 text-sm mb-3">Note interne ({(dosar.note_interne || []).length})</p>
          {(dosar.note_interne || []).length === 0 ? (
            <p className="text-gray-500 text-sm">Nicio nota.</p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {(dosar.note_interne || []).map((n, i) => (
                <div key={`${n.data}-${i}`} className="p-3 bg-gray-700 rounded-lg">
                  <div className="flex justify-between text-xs text-gray-400 mb-1">
                    <span className="font-medium text-emerald-400">{n.autor}</span>
                    <span>{n.data ? n.data.slice(0, 10) : ""}</span>
                  </div>
                  <p className="text-white text-sm">{n.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 pt-6 border-t border-gray-700">
          <p className="text-amber-400 text-sm mb-3">Note vizibile client ({(dosar.note_vizibile || []).length})</p>
          {(dosar.note_vizibile || []).length === 0 ? (
            <p className="text-gray-500 text-sm">Nicio nota vizibila.</p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {(dosar.note_vizibile || []).map((n, i) => (
                <div key={`vis-${n.data}-${i}`} className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                  <div className="flex justify-between text-xs text-gray-400 mb-1">
                    <span className="font-medium text-amber-400">{n.autor}</span>
                    <span>{n.data ? n.data.slice(0, 10) : ""}</span>
                  </div>
                  <p className="text-white text-sm">{n.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 pt-6 border-t border-gray-700">
          <button onClick={onClose} className="w-full h-12 bg-gray-700 text-white rounded-full hover:bg-gray-600">Inchide</button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
